import { modalUpdateAsync, switchPlay } from 'actions/annotation-actions';
import { Button } from 'antd';
import { getCore } from 'cvat-core-wrapper';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { connect } from 'react-redux';
import { CombinedState } from 'reducers';
import { getTimelineFrameRate, secondsToFrame, getRationalFps, frameToMs } from 'utils/media-time';
import { parseAndUpgradeAttribute } from 'utils/provenance-adapter';
import { toDisplay_V2 } from '../phase2-annotation-conf';
import { toDisplay_V1 } from '../phase2-annotation-conf_v1';
import { toDisplay_phase3 } from '../phase3-annotation-conf';
import { FormOutlined } from '@ant-design/icons';
import { computeTranscriptCheckpointFrames } from './transcript-checkpoints';
const core = getCore();
interface StateToProps {
    frameNumber: number;
    people: any[];
    modalVisible: boolean;
    modalMode: string;
    playing: boolean;
    jobPhase: string;
    transcriptData: any;
    dataFrameRate: number | null;
    startFrame: number;
    stopFrame: number;
    jobAttributeSpec: any;
    jobAttributes: any;
    phase2a_version: string;
}

interface DispatchToProps {
    onSwitchPlay: (playing: boolean) => void;
    showPersonModal: (people: any[], mode: string) => void;
    hidePersonModal: () => void;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: {
                instance: { phase: jobPhase, startFrame, stopFrame, phase2a_version, dataFrameRate },
                attributes: { self: jobAttributeSpec },
            },
            modal: { mode: modalMode, visible: modalVisible },
            annotations: { allStates, jobAttributes },
            player: {
                playing,
                frame: { number: frameNumber },
                transcript: { data: transcriptData },
            },
        },
    } = state;
    return {
        jobPhase,
        frameNumber,
        modalMode,
        modalVisible,
        people: allStates.filter((state) => state.label.name.startsWith('person:')),
        playing,
        transcriptData,
        dataFrameRate,
        startFrame,
        stopFrame,
        jobAttributes,
        jobAttributeSpec,
        phase2a_version,
    };
}

function mapDispatchToProps(dispatch: any): DispatchToProps {
    return {
        showPersonModal(people: any[], mode: string): void {
            dispatch(
                modalUpdateAsync({
                    people,
                    visible: true,
                    mode,
                }),
            );
        },
        hidePersonModal(): void {
            dispatch(
                modalUpdateAsync({
                    people: [],
                    visible: false,
                    mode: '',
                }),
            );
        },
        onSwitchPlay(play: boolean): void {
            dispatch(switchPlay(play));
        },
    };
}

function AutoPopup(props: StateToProps & DispatchToProps) {
    const {
        frameNumber,
        jobPhase,
        playing,
        modalMode,
        modalVisible,
        people,
        transcriptData,
        dataFrameRate,
        startFrame,
        stopFrame,
        onSwitchPlay,
        showPersonModal,
        hidePersonModal,
        jobAttributeSpec,
        jobAttributes,
        phase2a_version,
    } = props;
    const frameRate = getTimelineFrameRate(dataFrameRate);
    const { fpsNum, fpsDen, isStable } = getRationalFps(dataFrameRate || 15);

    const [popupFrames, setPopupFrames] = useState<number[]>([]);
    const [initialFrameRate] = useState(frameRate);
    const aboutToShowPopup = useRef<boolean>(false);

    // set toDisplay based on current phase and phase2A version
    const toDisplay = jobPhase === 'phase3a' ? toDisplay_phase3 : phase2a_version === '2' ? toDisplay_V2 : toDisplay_V1;

    // If we use the `useCallback` hook here, the function returns stale values.
    const isRecurring = (idx: number) => {
        if (idx === popupFrames.length - 1) return false;
        return true;
    }

    const getAttrVal = (attributes: any, attrId: number, idx: number) => {
        return isRecurring(idx)
            ? getRecurringVal(attributes[attrId], popupFrames[idx])
            : attributes[attrId];
    };

    const getRecurringVal = useCallback((val: string, currentFrame: number) => {
        const payload = parseAndUpgradeAttribute(
            val,
            startFrame,
            fpsNum,
            fpsDen,
            isStable
        );
        if (payload.schema_version === -1) {
            return '';
        }
        const localTargetMs = frameToMs(currentFrame, startFrame, fpsNum, fpsDen);
        const match = payload.entries.find(
            (entry) => Math.abs(entry.target_ms - localTargetMs) < 1000
        );
        return match ? match.value : '';
    }, [startFrame, fpsNum, fpsDen, isStable]);

    // This function sets the doesNotAppearDefault values for phase2b popups based on phase2a popup values
    const setDoesNotAppearValues = (People: any, allPopupFrames: any) => {

        const phase2aFrames = allPopupFrames.phase2a.map((end: any, index: number, arr: any[]) => ({
            start: index === 0 ? 0 : arr[index - 1],
            end,
        }));

        // Creates a map of the doesNotAppear values for each person
        let doesNotAppearMap: any = {};
        people.forEach((state) => {
            const doesNotAppearAttrs = state.label.attributes.filter((attr: any) => {
                return attr.name.startsWith('phase2a') && attr.name.endsWith('.doesnotappear');
            });
            let doesNotAppearVals: any = [];

            // const doesNotAppearVals = doesNotAppearAttrs.map((attr: any) => state.attributes[attr.id]);
            // The above one line approach does not work since it does not preserve the value ordering.
            // It was leading to the wrong values being assigned to the wrong popups.

            // Instead we use the below approach to preserve the ordering of the values.

            // We must iterate over the attributes & put the values into an array based on the popup number
            // popup 0 -> doesNotAppearVals[0], popup 1 -> doesNotAppearVals[1], etc.

            doesNotAppearAttrs.forEach((attr: any) => {
                let name = attr.name.split('.');
                let popupNumber = parseInt(name[2]);
                let doesNotAppearVal = state.attributes[attr.id];
                // makes sure null values are set to false
                doesNotAppearVals[popupNumber] = doesNotAppearVal === 'true' ? 'true' : 'false';
            });


            let doesNotContributeVals: any = [];
            const doesNotContributeAttrs = state.label.attributes.filter((attr: any) => {
                return attr.name.startsWith('phase2a') && attr.name.endsWith('.doesnotcontribute');
            });
            doesNotContributeAttrs.forEach((attr: any) => {
                let name = attr.name.split('.');
                let popupNumber = parseInt(name[2]);
                let doesNotContributeVal = state.attributes[attr.id];
                // makes sure null values are set to false
                doesNotContributeVals[popupNumber] = doesNotContributeVal === 'true' ? 'true' : 'false';
            });

            let bitwiseOr = doesNotAppearVals.map((val: any, index: number) => (val === 'true' ? true : false) || (doesNotContributeVals[index] === 'true' ? true : false));

            doesNotAppearMap[state.serverID] = bitwiseOr;
        });

        let phase2bMap: any = {};
        allPopupFrames.phase2b.forEach((value: number) => {
            for (let i = 0; i < phase2aFrames.length; i++) {
                const frame = phase2aFrames[i];
                if (value > frame.start && value <= frame.end) {
                    phase2bMap[value] = i;
                    break;
                }
            }
        });

        people.forEach((state) => {
            let serverID = state.serverID;
            let doesNotAppearVals = doesNotAppearMap[serverID];
            let peopleMap: any = {};
            allPopupFrames.phase2b.forEach((value: number) => {
                let indexToCheck = phase2bMap[value];
                let doesNotAppearVal = doesNotAppearVals[indexToCheck];
                peopleMap[value] = doesNotAppearVal;
            });
            state.doesNotAppearDefault = JSON.stringify(peopleMap);
            // Pre setting the values ( like done below ) does not work since the values will not get assigned again after they've been preset.
            // These values must be set in phase2b popup questionnaire.tsx
                // let doesNotAppearAttrs = state.label.attributes.filter((attr: any) => attr.name.endsWith('.doesnotappear'));
                // let doesNotAppearAttr = doesNotAppearAttrs[3];
                // state.attributes[doesNotAppearAttr.id] = JSON.stringify(peopleMap);
        });
    };

    // This effect calculates the popup frames based on the first utterance times of civilians and police
    useEffect(() => {
        if (jobPhase === core.enums.Phase.PHASE2A) {
            setPopupFrames(computeTranscriptCheckpointFrames({
                mode: 'phase2a',
                transcriptData,
                people,
                startFrame,
                stopFrame,
                frameRate,
            }));
        } else if (jobPhase === core.enums.Phase.PHASE2B) {
            setPopupFrames(computeTranscriptCheckpointFrames({
                mode: 'phase2b',
                transcriptData,
                people,
                startFrame,
                stopFrame,
                frameRate: initialFrameRate,
            }));
        } else if (jobPhase === core.enums.Phase.PHASE3A) {
            setPopupFrames(computeTranscriptCheckpointFrames({
                mode: 'phase2a',
                transcriptData,
                people,
                startFrame,
                stopFrame,
                frameRate,
            }));
        }
    }, [frameRate, initialFrameRate, jobPhase, people, startFrame, stopFrame, transcriptData]);

    // This effect calls the setDoesNotAppearValues function when in phase2b
    useEffect(() => {
        if (jobPhase === core.enums.Phase.PHASE2B && popupFrames.length > 0 && transcriptData) {
            // We recalculate phase2a popup frames here so that we can filter people based on the phase2b popup frames
            const phase2aPopup1Frame = Math.floor((popupFrames[0] + (stopFrame - 1)) / 2);

            const phase2aPopup2Frame = stopFrame - 1;

            let allPopupFrames = {
                phase2a: [popupFrames[0], phase2aPopup1Frame, phase2aPopup2Frame],
                phase2b: popupFrames,
            };
            setDoesNotAppearValues(people, allPopupFrames);
        }
    }, [popupFrames, jobPhase, transcriptData]);

    useEffect(() => {
        if (jobPhase === core.enums.Phase.PHASE2A) {
            // find the lowest index to show
            const indexToShow = popupFrames.findIndex(
                (popupFrame, idx) =>
                    frameNumber >= popupFrame &&
                    people.some(
                        (state) =>
                            // doesnotappear is unchecked, if it exists
                            state.label.attributes.every((attr: any) =>
                                attr.name === 'phase2a.popup.' + idx + '.doesnotappear'
                                    ? ['false', ''].includes(state.attributes[attr.id])
                                    : true,
                            ) &&
                            // doesnotcontribute is unchecked, if it exists
                            state.label.attributes.every((attr: any) =>
                                attr.name === 'phase2a.popup.' + idx + '.doesnotcontribute'
                                    ? ['false', ''].includes(state.attributes[attr.id])
                                    : true,
                            ) &&
                            // non-nullable attributes are unfilled
                            state.label.attributes
                                .filter(
                                    (attr: any) =>
                                        attr.name.startsWith('phase2a.popup.' + idx + '.') &&
                                        !attr.nullable &&
                                        toDisplay(
                                            state.label.attributes,
                                            jobAttributeSpec,
                                            state.attributes,
                                            jobAttributes,
                                            attr,
                                            people,
                                        ),
                                )
                                .some((attr: any) => state.attributes[attr.id] === ''),
                    ),
            );

            if (indexToShow > -1 && !(modalMode === 'phase2a-auto-popup_' + indexToShow) && !aboutToShowPopup.current) {
                onSwitchPlay(false);
                aboutToShowPopup.current = true;
                showPersonModal(
                    people
                        .filter((state) => state.label.name.startsWith('person:'))
                        .map((person) => ({
                            clientID: person.clientID,
                            frameNumber: person.frame,
                        })),
                    'phase2a-auto-popup_' + indexToShow,
                );
            } else if (indexToShow === -1) {
                // hidePersonModal();
                aboutToShowPopup.current = false;
            }
        } else if (jobPhase === core.enums.Phase.PHASE2B) {
            const indexToShow = popupFrames.findIndex((popupFrame, idx) => {
                const attrNameStart = 'phase2b.popup.' + (isRecurring(idx) ? 'recurring' : '0') + '.'
                return (
                    frameNumber >= popupFrame &&
                    people.some((state) => {
                        const phase2bAttrs = state.label.attributes.filter((attr: any) =>
                            attr.name.startsWith(attrNameStart),
                        );

                        // does not appear is unchecked
                        const doesNotAppearCond = phase2bAttrs.every((attr: any) =>
                            attr.name.endsWith('.doesnotappear')
                                ? ['false', ''].includes(
                                      isRecurring(idx)
                                          ? getRecurringVal(state.attributes[attr.id], popupFrame)
                                          : state.attributes[attr.id],
                                  )
                                : true,
                        );
                        // non-nullable attributes are unfilled
                        const nonNullableCond = phase2bAttrs
                            .filter((attr: any) => !attr.nullable)
                            .some(
                                (attr: any) =>
                                    (isRecurring(idx)
                                        ? getRecurringVal(state.attributes[attr.id], popupFrame)
                                        : state.attributes[attr.id]) === '',
                            );
                        return doesNotAppearCond && nonNullableCond;
                    })
                );
            });

            // console.log('indexToShow' + indexToShow);

            if (indexToShow === -1) {
                // hidePersonModal();
                aboutToShowPopup.current = false;
            } else if (indexToShow == popupFrames.length - 1) {
                // non-recurring
                if (!(modalMode === 'phase2b-auto-popup_' + 0) && !aboutToShowPopup.current) {
                    onSwitchPlay(false);
                    aboutToShowPopup.current = true;
                    showPersonModal(
                        people
                            .filter((state) => state.label.name.startsWith('person:'))
                            .map((person) => ({
                                clientID: person.clientID,
                                frameNumber: person.frame,
                            })),
                        'phase2b-auto-popup_' + 0,
                    );
                }
            } else {
                // recurring
                if (
                    !(modalMode === 'phase2b-auto-popup_recurring_' + popupFrames[indexToShow]) &&
                    !aboutToShowPopup.current
                ) {
                    onSwitchPlay(false);
                    aboutToShowPopup.current = true;
                    showPersonModal(
                        people
                            .filter((state) => state.label.name.startsWith('person:'))
                            .map((person) => ({
                                clientID: person.clientID,
                                frameNumber: person.frame,
                            })),
                        'phase2b-auto-popup_recurring_' + popupFrames[indexToShow],
                    );
                }
            }
        } else if (jobPhase === core.enums.Phase.PHASE3A) {
            // find the lowest index to show
            const indexToShow = popupFrames.findIndex(
                (popupFrame, idx) =>
                    frameNumber >= popupFrame &&
                    people.some(
                        (state) =>
                            // doesnotappear is unchecked, if it exists
                            state.label.attributes.every((attr: any) =>
                                attr.name === 'phase3a.popup.' + idx + '.doesnotappear'
                                    ? ['false', ''].includes(state.attributes[attr.id])
                                    : true,
                            ) &&
                            // doesnotcontribute is unchecked, if it exists
                            state.label.attributes.every((attr: any) =>
                                attr.name === 'phase3a.popup.' + idx + '.doesnotcontribute'
                                    ? ['false', ''].includes(state.attributes[attr.id])
                                    : true,
                            ) &&
                            // non-nullable attributes are unfilled
                            state.label.attributes
                                .filter(
                                    (attr: any) =>
                                        attr.name.startsWith('phase3a.popup.' + idx + '.') &&
                                        !attr.nullable &&
                                        toDisplay(
                                            state.label.attributes,
                                            jobAttributeSpec,
                                            state.attributes,
                                            jobAttributes,
                                            attr,
                                            people,
                                        ),
                                )
                                .some((attr: any) => state.attributes[attr.id] === ''),
                    ),
            );

            if (indexToShow > -1 && !(modalMode === 'phase3a-auto-popup_' + indexToShow) && !aboutToShowPopup.current) {
                onSwitchPlay(false);
                aboutToShowPopup.current = true;
                showPersonModal(
                    people
                        .filter((state) => state.label.name.startsWith('person:'))
                        .map((person) => ({
                            clientID: person.clientID,
                            frameNumber: person.frame,
                        })),
                    'phase3a-auto-popup_' + indexToShow,
                );
            } else if (indexToShow === -1) {
                // hidePersonModal();
                aboutToShowPopup.current = false;
            }
        }
    }, [frameNumber, jobPhase, popupFrames]);

    useEffect(() => {
        aboutToShowPopup.current = false;
    }, [frameNumber]);

    const getPopupPeoplePayload = useCallback(() => (
        people
            .filter((state) => {
                if (state.label.name.startsWith('person:')) {
                    return true;
                }
                return false;
            })
            .map((person) => ({
                clientID: person.clientID,
                frameNumber: person.frame,
            }))
    ), [people]);

    const openPopup = useCallback((mode: string) => {
        onSwitchPlay(false);
        showPersonModal(getPopupPeoplePayload(), mode);
    }, [getPopupPeoplePayload, onSwitchPlay, showPersonModal]);

    const renderPopupButton = (
        idx: number,
        mode: string,
        frame: number,
        recurring = false,
    ): JSX.Element => (
        <div className='cvat-auto-popup-row' key={mode}>
            <Button
                block
                className='cvat-auto-popup-button'
                icon={<FormOutlined />}
                onClick={() => {
                    openPopup(mode);
                }}
            >
                <span className='cvat-auto-popup-button-content'>
                    <span className='cvat-auto-popup-button-title'>Open Popup #{idx + 1}</span>
                    <span className='cvat-auto-popup-button-separator' aria-hidden='true'>•</span>
                    <span className='cvat-auto-popup-button-meta'>
                        Frame {frame}{recurring ? ' • Recurring' : ''}
                    </span>
                </span>
            </Button>
        </div>
    );

    return (
        <>
            {jobPhase === core.enums.Phase.PHASE2A && (
                <div className='cvat-auto-popup-list'>
                    {popupFrames.map(
                        (_, idx) =>
                            people.every(
                                (state) =>
                                    // does not appear is checked
                                    state.label.attributes.find(
                                        (attr: any) =>
                                            attr.name === 'phase2a.popup.' + idx + '.doesnotappear' &&
                                            ['true'].includes(state.attributes[attr.id]),
                                    ) ||
                                    // does not contribute is checked
                                    state.label.attributes.find(
                                        (attr: any) =>
                                            attr.name === 'phase2a.popup.' + idx + '.doesnotcontribute' &&
                                            ['true'].includes(state.attributes[attr.id]),
                                    ) ||
                                    // non-nullable attributes are filled
                                    state.label.attributes
                                        .filter(
                                            (attr: any) =>
                                                attr.name.startsWith('phase2a.popup.' + idx + '.') &&
                                                !attr.nullable &&
                                                toDisplay(
                                                    state.label.attributes,
                                                    jobAttributeSpec,
                                                    state.attributes,
                                                    jobAttributes,
                                                    attr,
                                                    people,
                                                ),
                                        )
                                        .some((attr: any) => state.attributes[attr.id] !== ''),
                            ) &&
                            renderPopupButton(
                                idx,
                                'phase2a-auto-popup_' + idx,
                                popupFrames[idx],
                            ),
                    )}
                </div>
            )}

            {jobPhase === core.enums.Phase.PHASE2B && (
                <div className='cvat-auto-popup-list'>
                    {popupFrames.map(
                        (_, idx) =>
                            people.every(
                                (state) =>
                                    // does not appear is checked
                                    state.label.attributes.find(
                                        (attr: any) =>
                                            attr.name === 'phase2b.popup.recurring.doesnotappear' &&
                                            ['true'].includes(getAttrVal(state.attributes, attr.id, idx)),
                                    ) ||
                                    // check if non-nullable attributes are filled
                                    state.label.attributes
                                        .filter(
                                            (attr: any) =>
                                                attr.name.startsWith('phase2b.popup.' + (isRecurring(idx) ? 'recurring' : '0') + '.') &&
                                                !attr.nullable &&
                                                toDisplay(
                                                    state.label.attributes,
                                                    jobAttributeSpec,
                                                    state.attributes,
                                                    jobAttributes,
                                                    attr,
                                                    people,
                                                ),
                                        )
                                        .some((attr: any) => getAttrVal(state.attributes, attr.id, idx) !== ''),
                            ) &&
                            renderPopupButton(
                                idx,
                                'phase2b-auto-popup_' + (isRecurring(idx) ? 'recurring_' + popupFrames[idx] : 0),
                                popupFrames[idx],
                                isRecurring(idx),
                            ),
                    )}
                </div>
            )}

            {jobPhase === core.enums.Phase.PHASE3A && (
                <div className='cvat-auto-popup-list'>
                    {popupFrames.map(
                        (_, idx) =>
                            people.every(
                                (state) =>
                                    // does not appear is checked
                                    state.label.attributes.find(
                                        (attr: any) =>
                                            attr.name === 'phase3a.popup.' + idx + '.doesnotappear' &&
                                            ['true'].includes(state.attributes[attr.id]),
                                    ) ||
                                    // does not contribute is checked
                                    state.label.attributes.find(
                                        (attr: any) =>
                                            attr.name === 'phase3a.popup.' + idx + '.doesnotcontribute' &&
                                            ['true'].includes(state.attributes[attr.id]),
                                    ) ||
                                    // non-nullable attributes are filled
                                    state.label.attributes
                                        .filter(
                                            (attr: any) =>
                                                attr.name.startsWith('phase3a.popup.' + idx + '.') &&
                                                !attr.nullable &&
                                                toDisplay(
                                                    state.label.attributes,
                                                    jobAttributeSpec,
                                                    state.attributes,
                                                    jobAttributes,
                                                    attr,
                                                    people,
                                                ),
                                        )
                                        .some((attr: any) => state.attributes[attr.id] !== ''),
                            ) &&
                            renderPopupButton(
                                idx,
                                'phase3a-auto-popup_' + idx,
                                popupFrames[idx],
                            ),
                    )}
                </div>
            )}
        </>
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(AutoPopup);
